import { _decorator, Component, Texture2D, Sprite, SpriteFrame, sys } from 'cc';
import Delay from '../../Utils/Delay';
import VideoElementGenerator from '../DOM/VideoElementGenerator';

const { ccclass, requireComponent, property } = _decorator;

/**
 * HTML Video Element 原生事件類型
 * 這些事件對應 HTMLVideoElement 的原生事件
 */
export enum VideoElementEvents {
    // 加載與緩衝相關事件
    LOAD_START = 'loadstart', // 開始加載
    PROGRESS = 'progress', // 加載進度更新
    SUSPEND = 'suspend', // 暫停加載
    ABORT = 'abort', // 中止加載
    ERROR = 'error', // 加載錯誤
    STALLED = 'stalled', // 加載停滯
    LOADED_METADATA = 'loadedmetadata', // 元數據加載完成
    LOADED_DATA = 'loadeddata', // 數據加載完成
    CAN_PLAY = 'canplay', // 可以播放
    CAN_PLAY_THROUGH = 'canplaythrough', // 可以流暢播放
    WAITING = 'waiting', // 等待數據

    // 播放控制相關事件
    PLAY = 'play', // 開始播放
    PLAYING = 'playing', // 正在播放
    PAUSE = 'pause', // 暫停
    ENDED = 'ended', // 播放結束
    RATE_CHANGE = 'ratechange', // 播放速率改變
    TIME_UPDATE = 'timeupdate', // 播放時間更新
    SEEKED = 'seeked', // 尋找完成
    SEEKING = 'seeking', // 正在尋找

    // 音量與其他事件
    VOLUME_CHANGE = 'volumechange', // 音量改變
    DURATION_CHANGE = 'durationchange', // 播放時長改變
    RESIZE = 'resize', // 視頻尺寸改變（部分瀏覽器支援）
}

enum VideoElementReadyState {
    HAVE_NOTHING = 0,
    HAVE_METADATA = 1,
    HAVE_CURRENT_DATA = 2,
    HAVE_FUTURE_DATA = 3,
    HAVE_ENOUGH_DATA = 4,
}

enum EventType {
    VideoPlay = 'VideoPlay',
    VideoStartPlaying = 'VideoStartPlaying',
    VideoEnded = 'VideoEnded',
    UpdateFirstFrame = 'UpdateFirstFrame',
}

/**
 * 視頻播放基底類別
 * 提供基本的視頻渲染功能和生命週期管理
 */
@ccclass('VideoSprite')
@requireComponent(Sprite)
export default class VideoSprite extends Component {
    public static readonly EVENT_TYPE = EventType;

    @property(VideoElementGenerator)
    private readonly videoGenerator: VideoElementGenerator = null;

    private _init: boolean = false;
    private _sprite: Sprite = null;
    private _texture: Texture2D = null;
    private _videoElement: HTMLVideoElement = null;
    private _canvas: HTMLCanvasElement = null;
    private _canvasContent2D: CanvasRenderingContext2D = null;
    private _onDataLoadedCallback: () => void = this.updateFirstFrame.bind(this);
    private _onVideoStartPlayingCallback: () => void = this.onVideoStartPlaying.bind(this);
    private _onVideoEndedCallback: () => void = this.onVideoEnded.bind(this);

    public get videoElement(): HTMLVideoElement {
        return this._videoElement;
    }
    public set videoElement(value: HTMLVideoElement) {
        if (this._videoElement === value) {
            return;
        }
        if (this._videoElement) {
            this._init = false;
            this._videoElement.removeEventListener(VideoElementEvents.LOADED_DATA, this._onDataLoadedCallback);
            this._videoElement.removeEventListener(VideoElementEvents.PLAY, this._onVideoStartPlayingCallback);
            this._videoElement.removeEventListener(VideoElementEvents.ENDED, this._onVideoEndedCallback);
        }
        this._videoElement = value;
    }

    public get sprite(): Sprite {
        if (!this._sprite) {
            this._sprite = this.getComponent(Sprite);
        }
        return this._sprite;
    }

    public get isPlaying(): boolean {
        return !!this.videoElement && !this.videoElement.paused && !this.videoElement.ended;
    }

    public get duration(): number {
        return this.videoElement ? this.videoElement.duration : 0;
    }

    public get currentTime(): number {
        return this.videoElement ? this.videoElement.currentTime : 0;
    }
    public set currentTime(value: number) {
        if (this.videoElement) {
            this.videoElement.currentTime = value;
            if (!this.isPlaying) {
                this.updateTexture();
            }
        }
    }

    protected get shouldUpdateTexture(): boolean {
        return this.sprite.node.active && this._texture && this.videoElement?.readyState >= VideoElementReadyState.HAVE_CURRENT_DATA;
    }

    private get canvas(): HTMLCanvasElement {
        if (!this._canvas) {
            this._canvas = document.createElement('canvas');
        }
        return this._canvas;
    }

    private get canvasContent2D(): CanvasRenderingContext2D {
        if (!this._canvasContent2D) {
            this._canvasContent2D = this.canvas.getContext('2d');
        }
        return this._canvasContent2D;
    }

    public onLoad() {
        if (this.videoGenerator) {
            this.videoElement = this.videoGenerator.element;
        }
    }

    public onDestroy() {
        if (this.videoElement) {
            this.videoElement.removeEventListener(VideoElementEvents.LOADED_DATA, this._onDataLoadedCallback);
            this.videoElement.removeEventListener(VideoElementEvents.PLAY, this._onVideoStartPlayingCallback);
            this.videoElement.removeEventListener(VideoElementEvents.ENDED, this._onVideoEndedCallback);
        }
    }

    public update() {
        if (this.isPlaying) {
            this.updateTexture();
        }
    }

    public play(loop?: boolean, time?: number) {
        if (!this.videoElement) {
            return Promise.resolve();
        }
        this.emit(VideoSprite.EVENT_TYPE.VideoPlay);

        // ios 省電模式下, 太久沒有交互時播放影片會失敗 (且滑動事件不被承認交互)
        // 因此需防止播放失敗的情況, 避免直接輸出成 unhandledrejection 或讓流程卡住
        // 最好配合 VideoElementGenerator 確保播放器盡早處於可用狀態
        // (測試結果為該<video>曾經正常播出影片, 則後續播影片都不會有問題)
        const promise = Promise.race([Delay.event(VideoSprite.EVENT_TYPE.VideoPlay, this), Delay.event(VideoSprite.EVENT_TYPE.VideoEnded, this)]);
        let videoPlayPromiseCallback: () => void;
        const videoPlayPromise = new Promise<void>((resolve) => {
            videoPlayPromiseCallback = resolve;
        });
        this.videoElement.play().catch(() => {
            videoPlayPromiseCallback();
        });

        if (time !== undefined) {
            this.videoElement.currentTime = time;
        }
        if (loop !== undefined) {
            this.videoElement.loop = loop;
        }
        if (!this._init) {
            this.setupVideoElement();
            this._init = true;
        }
        return Promise.race([promise, videoPlayPromise]);
    }

    public pause(): void {
        this.videoElement?.pause();
    }

    public stop(): void {
        this.videoElement?.pause();
    }

    public load(): void {
        this.videoElement?.load();
    }

    protected setupTexture(): void {
        const { sprite } = this;
        if (!sprite.spriteFrame) {
            sprite.spriteFrame = new SpriteFrame();
            sprite.spriteFrame.packable = false;
        }
        this._texture = new Texture2D();
        this.resetTexture(this._texture);
        sprite.spriteFrame.texture = this._texture;
        // @ts-expect-error ensure sprite size is synced with video size
        sprite._applySpriteSize();
    }

    protected updateTexture(): void {
        if (!this.shouldUpdateTexture || !this._init) {
            return;
        }
        if (this.videoElement.videoWidth !== this._texture.width || this.videoElement.videoHeight !== this._texture.height) {
            this.resetTexture();
        }
        if (sys.os === sys.OS.IOS) {
            this._texture.uploadData(this.videoElement as any);
        } else {
            const { canvas, canvasContent2D } = this;
            canvas.width = this._texture.width;
            canvas.height = this._texture.height;
            canvasContent2D.drawImage(this.videoElement, 0, 0, canvas.width, canvas.height);
            this._texture.uploadData(canvas);
        }
    }

    protected setupVideoElement(): void {
        if (this.videoElement.readyState >= VideoElementReadyState.HAVE_CURRENT_DATA) {
            this.updateFirstFrame();
        } else {
            this.videoElement.addEventListener(VideoElementEvents.LOADED_DATA, this._onDataLoadedCallback);
        }
        this.videoElement.addEventListener(VideoElementEvents.PLAY, this._onVideoStartPlayingCallback);
        this.videoElement.addEventListener(VideoElementEvents.ENDED, this._onVideoEndedCallback);
    }

    protected resetVideoElementSetup(): void {
        if (this.videoElement) {
            this.videoElement.removeEventListener(VideoElementEvents.LOADED_DATA, this._onDataLoadedCallback);
            this.videoElement.removeEventListener(VideoElementEvents.PLAY, this._onVideoStartPlayingCallback);
            this.videoElement.removeEventListener(VideoElementEvents.ENDED, this._onVideoEndedCallback);
        }
        this._init = false;
    }

    protected updateFirstFrame() {
        this.setupTexture();
        this.updateTexture();
        this.emit(VideoSprite.EVENT_TYPE.UpdateFirstFrame);
    }

    protected onVideoStartPlaying() {
        this.emit(VideoSprite.EVENT_TYPE.VideoStartPlaying);
    }

    protected onVideoEnded() {
        this.emit(VideoSprite.EVENT_TYPE.VideoEnded);
    }

    /**
     * 重置貼圖屬性
     * @param texture 貼圖
     */
    protected resetTexture(texture: Texture2D = this._texture): void {
        if (!texture) {
            return;
        }
        texture.setFilters(Texture2D.Filter.LINEAR, Texture2D.Filter.LINEAR);
        texture.setMipFilter(Texture2D.Filter.LINEAR);
        texture.setWrapMode(Texture2D.WrapMode.CLAMP_TO_EDGE, Texture2D.WrapMode.CLAMP_TO_EDGE);
        texture.reset({
            width: this.videoElement?.videoWidth ?? 0,
            height: this.videoElement?.videoHeight ?? 0,
            // 從 VideoElement 獲取格式，默認使用 RGB888 格式
            format: Texture2D.PixelFormat.RGBA8888,
        });
    }
}
